import { Dialect } from '../dialect.js';
import type { Declaration, GlossaProgram, LValue, Program, Statement } from '../parser/ast.js';
import { normalizeKeywordText } from '../lexer/keywords.js';
import { BUILTIN_MATH_FUNCTION_NAMES, RESERVED_FUNCTION_IDENTIFIERS } from './interpreter.js';

/**
 * Ένα υποπρόγραμμα δεν επιτρέπεται να έχει το ΙΔΙΟ όνομα με μια έτοιμη
 * μαθηματική συνάρτηση (ΗΜ/ΣΥΝ/ΕΦ/ΛΟΓ/Ε/Α_Μ/Α_Τ/Τ_Ρ) — αλλιώς η κλήση
 * `όνομα(...)` καταλήγει ΠΑΝΤΑ στην έτοιμη συνάρτηση (βλ.
 * `Interpreter.evaluateFunctionCall`, ελέγχει πρώτα τα built-ins), κάνοντας
 * το υποπρόγραμμα του μαθητή σιωπηλά dead code — χωρίς αυτόν τον έλεγχο δεν
 * θα εμφανιζόταν ΚΑΝΕΝΑ σφάλμα. Βλ. `Interpreter.registerSubprograms` για
 * το runtime αντίστοιχο (τρέχει στην εγγραφή του υποπρογράμματος).
 */
export interface BuiltinFunctionNameCollision {
  readonly name: string;
  readonly line: number;
  readonly column: number;
}

/**
 * Επιστρέφει την ΠΡΩΤΗ σύγκρουση ονόματος (με σειρά εμφάνισης των
 * υποπρογραμμάτων στον κώδικα), ή `null` αν δεν υπάρχει καμία.
 */
export function findBuiltinFunctionNameCollision(program: GlossaProgram): BuiltinFunctionNameCollision | null {
  for (const sub of program.subprograms) {
    if (BUILTIN_MATH_FUNCTION_NAMES.has(normalizeKeywordText(sub.name))) {
      return { name: sub.name, line: sub.line, column: sub.column };
    }
  }
  return null;
}

export type ReservedNameRole = 'μεταβλητής' | 'πίνακα' | 'σταθεράς' | 'παραμέτρου';

export interface ReservedFunctionNameUse {
  readonly name: string;
  readonly role: ReservedNameRole;
  readonly line: number;
  readonly column: number;
}

/** true αν το όνομα (ανεξαρτήτως τόνων/πεζών-κεφαλαίων) είναι έτοιμη συνάρτηση που δεν επιτρέπεται ως όνομα μεταβλητής. */
export function isReservedFunctionName(name: string): boolean {
  return RESERVED_FUNCTION_IDENTIFIERS.has(normalizeKeywordText(name));
}

function checkDeclarations(declarations: readonly Declaration[]): ReservedFunctionNameUse | null {
  for (const decl of declarations) {
    if (decl.kind === 'ConstantDeclaration') {
      if (isReservedFunctionName(decl.name)) return { name: decl.name, role: 'σταθεράς', line: decl.line, column: decl.column };
      continue;
    }
    for (const declarator of decl.declarators) {
      if (isReservedFunctionName(declarator.name)) {
        return {
          name: declarator.name,
          role: declarator.dimensions.length > 0 ? 'πίνακα' : 'μεταβλητής',
          line: declarator.line ?? decl.line,
          column: declarator.column ?? decl.column,
        };
      }
    }
  }
  return null;
}

function checkTarget(target: LValue): ReservedFunctionNameUse | null {
  const id = target.kind === 'Identifier' ? target : target.array;
  if (!isReservedFunctionName(id.name)) return null;
  return { name: id.name, role: target.kind === 'Identifier' ? 'μεταβλητής' : 'πίνακα', line: target.line, column: target.column };
}

function checkStatements(statements: readonly Statement[]): ReservedFunctionNameUse | null {
  for (const stmt of statements) {
    let found: ReservedFunctionNameUse | null = null;
    switch (stmt.kind) {
      case 'AssignmentStatement':
        found = checkTarget(stmt.target);
        break;
      case 'ReadStatement':
        for (const target of stmt.targets) {
          found = checkTarget(target);
          if (found !== null) break;
        }
        break;
      case 'SwapStatement':
        found = checkTarget(stmt.left) ?? checkTarget(stmt.right);
        break;
      case 'ForStatement':
        found = checkTarget(stmt.variable) ?? checkStatements(stmt.body);
        break;
      case 'IfStatement': {
        found = checkStatements(stmt.thenBranch);
        for (const branch of stmt.elseIfBranches) found = found ?? checkStatements(branch.body);
        if (found === null && stmt.elseBranch !== null) found = checkStatements(stmt.elseBranch);
        break;
      }
      case 'WhileStatement':
      case 'RepeatUntilStatement':
        found = checkStatements(stmt.body);
        break;
      case 'SwitchStatement': {
        for (const switchCase of stmt.cases) found = found ?? checkStatements(switchCase.body);
        if (found === null && stmt.elseBody !== null) found = checkStatements(stmt.elseBody);
        break;
      }
      default:
        break;
    }
    if (found !== null) return found;
  }
  return null;
}

/**
 * Επιστρέφει την ΠΡΩΤΗ χρήση έτοιμης μαθηματικής συνάρτησης (ΗΜ/ΣΥΝ/ΕΦ/ΛΟΓ/
 * Ε/Α_Μ/Α_Τ/Τ_Ρ) ως ονόματος μεταβλητής, πίνακα, σταθεράς ή παραμέτρου, ή
 * `null` αν δεν υπάρχει. ΓΛΩΣΣΑ: ελέγχει τα τμήματα δηλώσεων και τις
 * παραμέτρους. Ψευδογλώσσα (χωρίς δηλώσεις): ελέγχει τα «Δεδομένα»/
 * «Αποτελέσματα» και κάθε στόχο ανάθεσης/ΔΙΑΒΑΣΕ/ΑΝΤΙΜΕΤΑΘΕΣΕ/ΓΙΑ. Τρέχει
 * ΠΡΙΝ την εκτέλεση, ώστε να πιάνει το λάθος και σε κλάδο που δεν θα
 * εκτελούνταν.
 */
export function findReservedFunctionNameAsIdentifier(program: Program): ReservedFunctionNameUse | null {
  if (program.dialect === Dialect.GLOSSA) {
    const main = checkDeclarations(program.declarations);
    if (main !== null) return main;
    for (const sub of program.subprograms) {
      for (const param of sub.parameters) {
        if (isReservedFunctionName(param)) return { name: param, role: 'παραμέτρου', line: sub.line, column: sub.column };
      }
      const result = checkDeclarations(sub.declarations);
      if (result !== null) return result;
    }
    return null;
  }
  for (const name of [...program.dataVariables, ...program.resultVariables]) {
    if (isReservedFunctionName(name)) return { name, role: 'μεταβλητής', line: program.line, column: program.column };
  }
  return checkStatements(program.body);
}
